import React, { useEffect, useCallback, useMemo, useRef, useState } from 'react'
import { connect } from 'react-redux'
import PageToolbar from './PageToolbar.js'
import redux from '../../redux/index.js'
import SearchField from '../SearchField.js'
import DeleteItem from '../dialogs/DeleteItem.js'
import TablePagination from './TablePagination.jsx'
import PageFooter from './PageFooter.jsx'
import { Link, styled } from '@pergas-common/pergas-components'
import { Table, Td } from '../../pergas-components/index.js'
import { Add, Email, OpenExternal, Teams } from '@pergas-common/pergas-icons'
import { createDateAccessor } from './columns.js'
import EditAccount from '../dialogs/EditAccount.js'
import { DeleteItemIcon, AccountIcon } from '../icons.js'
import Tooltip from '../Tooltip.js'
import {
  Button,
  Checkbox,
  FormControlLabel,
  FormGroup,
  IconButton,
  Popover,
  TextField
} from '@mui/material'
import FilterListIcon from '@mui/icons-material/FilterList'

const TENANT_FILTER_KEY = 'tenant_name'

const PageHolder = styled.div`
  display: flex;
  overflow: hidden;
  flex-direction: column;
  height: 100%;
  min-height: 0;
`

const TableHolder = styled.div`
  display: flex;
  overflow-y: scroll;
  flex-grow: 1;
  flex-basis: 50px;
  min-height: 0;
`

const NameHolder = styled.div`
  display: inline-block;
  margin: 0 8px;
`

const Spacer = styled.div`
  display: inline-block;
  margin-right: 3px;
`

const TenantHeaderHolder = styled.div`
  display: inline-flex;
  align-items: center;
`

const TenantFilterBody = styled.div`
  width: 280px;
  padding: 8px;
`

const TenantFilterActions = styled.div`
  display: flex;
  justify-content: space-between;
  padding-top: 8px;
`

const ClearFiltersHolder = styled.span`
  display: inline-flex;
  align-items: center;
  margin-left: 10px;
  line-height: 1;

  > div {
    display: inline-flex;
    align-items: center;
  }

  a {
    display: inline-flex;
    align-items: center;
  }

  svg {
    display: block;
  }
`

const AccountPage = ({
  locale,
  rows,
  limit,
  offset,
  orderBy,
  order,
  search,
  resetSearch,
  getPageItems,
  setOrder,
  setLimit,
  setOffset,
  setSearch,
  canUpdate,
  canDelete,
  deleteItem,
  onDeleteItemClick,
  onDeleteOk,
  onDeleteCancel,
  editItem,
  onEditItemClick,
  onEditOk,
  onEditCancel,
  canCreate,
  onShowAddDialog,
  filterQueries,
  setFilterQueries
}) => {
  useEffect(getPageItems, [limit, offset, orderBy, order, search, filterQueries])

  const setPageSizeRef = useRef(() => {})
  const [tenantFilterAnchorEl, setTenantFilterAnchorEl] = useState(null)
  const [tenantFilterSearch, setTenantFilterSearch] = useState('')
  const [tenantDraftSelection, setTenantDraftSelection] = useState([])

  const selectedTenants = useMemo(() => {
    return (filterQueries || [])
      .filter((q) => q.key === TENANT_FILTER_KEY && q.op === '=')
      .map((q) => q.value)
      .filter((value) => typeof value === 'string' && value.length > 0)
  }, [filterQueries])

  const hasActiveFilters = useMemo(() => {
    const hasSearch = typeof search === 'string' && search.trim() !== ''
    const hasFilterQueries = Array.isArray(filterQueries) && filterQueries.length > 0
    return hasSearch || hasFilterQueries
  }, [filterQueries, search])

  const clearAllFiltersLabel = locale.clear_all_filters || 'Clear all filters'

  const tenantOptions = useMemo(() => {
    const source = rows || []
    const unique = new Set()
    source.forEach((row) => {
      const value = row?.tenant_name
      if (typeof value === 'string' && value.trim() !== '') {
        unique.add(value.trim())
      }
    })
    return Array.from(unique).sort((a, b) => a.localeCompare(b, 'sv'))
  }, [rows])

  const filteredTenantOptions = useMemo(() => {
    const query = tenantFilterSearch.trim().toLowerCase()
    if (query === '') {
      return tenantOptions
    }
    return tenantOptions.filter((tenant) => tenant.toLowerCase().includes(query))
  }, [tenantFilterSearch, tenantOptions])

  const isTenantFilterOpen = Boolean(tenantFilterAnchorEl)

  const openTenantFilter = useCallback((event) => {
    event.preventDefault()
    event.stopPropagation()
    setTenantDraftSelection(selectedTenants)
    setTenantFilterSearch('')
    setTenantFilterAnchorEl(event.currentTarget)
  }, [selectedTenants])

  const closeTenantFilter = useCallback(() => {
    setTenantFilterAnchorEl(null)
  }, [])

  const clearAllFilters = useCallback(() => {
    resetSearch()
    setFilterQueries([])
    setTenantDraftSelection([])
    setTenantFilterSearch('')
    closeTenantFilter()
  }, [closeTenantFilter, resetSearch, setFilterQueries])

  const applyTenantFilter = useCallback(() => {
    const retained = (filterQueries || []).filter((q) => q.key !== TENANT_FILTER_KEY)
    const tenantQueries = tenantDraftSelection
      .filter((tenant) => typeof tenant === 'string' && tenant.length > 0)
      .map((tenant) => ({ key: TENANT_FILTER_KEY, value: tenant, op: '=' }))
    setFilterQueries([...retained, ...tenantQueries])
    closeTenantFilter()
  }, [closeTenantFilter, filterQueries, setFilterQueries, tenantDraftSelection])

  const toggleTenantDraftValue = useCallback((tenant) => {
    setTenantDraftSelection((prev) => {
      if (prev.includes(tenant)) {
        return prev.filter((value) => value !== tenant)
      }
      return [...prev, tenant]
    })
  }, [])

  const manualSort = useCallback(({ id, isSorted, isSortedDesc }) => {
    if (isSorted && !isSortedDesc) {
      setOrder(id, 'desc')
    } else if (isSorted && isSortedDesc) {
      setOrder('', '')
    } else {
      setOrder(id, 'asc')
    }
  }, [setOrder])

  const columns = useMemo(() => {
    return [
      {
        Header: locale.name,
        Cell: ({ cell }) => {
          const { row: { original } } = cell
          return (
            <Td
              {...cell.getCellProps()} left={
                <>
                  <AccountIcon style={{ width: 16, height: 16 }} />
                  <NameHolder>
                    {!!original.name && <span>{original.name}</span>}
                  </NameHolder>
                </>
            }
            />
          )
        },
        canSort: true,
        id: 'name',
        size: 'md',
        manualSort,
        sortType: () => {}
      },
      {
        id: 'permission',
        Header: locale.permission,
        Cell: ({ cell }) => {
          const { row: { original } } = cell
          const result = Array.isArray(original.permissions) && original.permissions.map(r => locale[r.name]).sort().join(', ')
          return (<Td {...cell.getCellProps()} left={result && result} />)
        },
        isSortable: false
      },
      {
        Header: (
          <TenantHeaderHolder>
            <span>{locale.tenant_name}</span>
            <IconButton
              size='small'
              onClick={openTenantFilter}
              title={locale.filter || locale.tenant_name}
            >
              <FilterListIcon
                fontSize='inherit'
                style={{ color: selectedTenants.length > 0 ? '#64B5F6' : '#FFFFFF' }}
              />
            </IconButton>
          </TenantHeaderHolder>
        ),
        manualSort,
        accessor: 'tenant_name',
        sortType: () => {}
      },
      {
        Header: locale.email,
        manualSort,
        accessor: 'email',
        sortType: () => {}
      },
      {
        id: 'updated_at',
        Header: locale.updated_at,
        manualSort,
        accessor: createDateAccessor('updated_at'),
        sortType: () => {}
      },
      {
        id: 'toolbar',
        Header: locale.tool_belt,
        Cell: ({ cell }) => {
          const { row: { original } } = cell
          const { email } = original
          return (
            <Td
              {...cell.getCellProps()}
              left={
                <>
                  {!!email && <Tooltip content={email}><Spacer><Link href={`mailto:${email}`}><Email width={18} height={18} color='#447fb1' /></Link></Spacer></Tooltip>}
                  {!!email && <Tooltip content='Teams'><Spacer><Link href={`https://teams.microsoft.com/l/call/0/0?users=${email}`}><Teams width={18} height={18} /></Link></Spacer></Tooltip>}
                  {canUpdate && <Tooltip content={locale.update}><Spacer><Link onClickHandler={() => { onEditItemClick(original) }}><OpenExternal color='#447fb1' width={18} height={18} /></Link></Spacer></Tooltip>}
                </>
              }
              right={canDelete && <Tooltip side='left' content={locale.delete}><Link onClickHandler={() => { onDeleteItemClick(original) }}><DeleteItemIcon /></Link></Tooltip>}
            />
          )
        },
        size: 'sm',
        isSortable: false
      }
    ]
  }, [locale, canUpdate, canDelete, manualSort, onDeleteItemClick, onEditItemClick, openTenantFilter, selectedTenants])

  let initialSortBy = []
  if (orderBy) {
    initialSortBy = [{
      id: orderBy,
      desc: order === 'desc'
    }]
  }

  const pagination = (
    <TablePagination
      limit={limit}
      offset={offset}
      onRowsPerPageChange={(e) => {
        setLimit(e.target.value)
        setPageSizeRef.current(e.target.value)
      }}
      onPageChange={(e, number) => setOffset(number * limit)}
    />
  )

  return (
    <PageHolder>
      {editItem && <EditAccount isEditing item={editItem} onOk={onEditOk} onCancel={onEditCancel} />}
      {deleteItem && <DeleteItem text={deleteItem.name || deleteItem.first_name} onOk={() => { onDeleteOk(deleteItem) }} onCancel={onDeleteCancel} />}
      <PageToolbar left={<><AccountIcon style={{ width: 20, height: 20 }} /><span>{locale.accounts}</span> {canCreate && <Link onClickHandler={onShowAddDialog}><Add width={20} height={20} color='#447fb1' /></Link>} {hasActiveFilters && <ClearFiltersHolder><Tooltip content={clearAllFiltersLabel}><span><Link onClickHandler={clearAllFilters}><FilterListIcon style={{ color: '#d32f2f' }} /></Link></span></Tooltip></ClearFiltersHolder>}</>}>
        <SearchField resetSearch={resetSearch} value={search} onChange={setSearch} />
      </PageToolbar>
      <TableHolder>
        <Table columnResizeStorageKey='account' columns={columns} data={rows} initialPageSize={limit} initialSortBy={initialSortBy}>
          {({ setPageSize }) => {
            // Store table callback in a ref so we can call it from elsewhere
            setPageSizeRef.current = setPageSize
          }}
        </Table>
      </TableHolder>
      <Popover
        open={isTenantFilterOpen}
        anchorEl={tenantFilterAnchorEl}
        onClose={closeTenantFilter}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
        disableEnforceFocus
        disableAutoFocus
      >
        <TenantFilterBody onClick={(e) => e.stopPropagation()}>
          <TextField
            fullWidth
            size='small'
            value={tenantFilterSearch}
            placeholder={locale.search || 'Search'}
            onChange={(e) => setTenantFilterSearch(e.target.value)}
          />
          <FormControlLabel
            control={
              <Checkbox
                checked={tenantDraftSelection.length === 0}
                onChange={() => setTenantDraftSelection([])}
              />
            }
            label={locale.all || 'All'}
          />
          <FormGroup style={{ maxHeight: 260, overflowY: 'auto' }}>
            {filteredTenantOptions.map((tenant) => (
              <FormControlLabel
                key={tenant}
                control={
                  <Checkbox
                    checked={tenantDraftSelection.includes(tenant)}
                    onChange={() => toggleTenantDraftValue(tenant)}
                  />
                }
                label={tenant}
              />
            ))}
          </FormGroup>
          <TenantFilterActions>
            <Button size='small' variant='contained' onClick={applyTenantFilter}>OK</Button>
          </TenantFilterActions>
        </TenantFilterBody>
      </Popover>
      <PageFooter>{pagination}</PageFooter>
    </PageHolder>
  )
}

const mapStateToProps = (state) => {
  const { locale, account } = state
  const permissions = state.login.permissions
  const {
    pageItems,
    limit,
    offset,
    orderBy,
    order,
    search,
    filterQueries
  } = account
  return {
    locale: locale.strings,
    rows: pageItems,
    limit,
    offset,
    orderBy,
    order,
    search,
    filterQueries,
    canUpdate: permissions.account.canUpdate,
    canDelete: permissions.account.canDelete,
    canCreate: permissions.account.canCreate,
    deleteItem: state.account.deleteItem,
    editItem: state.account.editItem
  }
}

const mapDispatchToProps = (dispatch) => {
  const { actions: { account } } = redux
  return {
    onShowAddDialog: () => {
      dispatch(account.showAddAccountDialog())
    },

    onEditOk: (a) => {
      dispatch(account.hideEditAccountDialog())
      dispatch(account.updateAccount(a))
    },
    onEditItemClick: (a) => {
      dispatch(account.showEditAccountDialog(a))
    },
    onEditCancel: () => {
      dispatch(account.hideEditAccountDialog())
    },

    onDeleteOk: (a) => {
      dispatch(account.hideDeleteAccountDialog())
      dispatch(account.deleteAccount(a))
    },
    onDeleteItemClick: (a) => {
      dispatch(account.showDeleteAccountDialog(a))
    },
    onDeleteCancel: () => {
      dispatch(account.hideDeleteAccountDialog())
    },

    getPageItems: () => dispatch(account.getPageItems()),
    setOrder: (orderBy, order) => dispatch(account.setOrder(orderBy, order)),
    setLimit: (limit) => dispatch(account.setLimit(limit)),
    setOffset: (offset) => dispatch(account.setOffset(offset)),
    setSearch: (search) => dispatch(account.setSearch(search)),
    resetSearch: () => dispatch(account.resetSearch()),
    setFilterQueries: (filterQueries) => dispatch(account.setFilterQueries(filterQueries))
  }
}

export default connect(mapStateToProps, mapDispatchToProps)(AccountPage)
